import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone, User, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';

const MemberProfileModal = ({ isOpen, onClose, member }) => {
  const carnetRef = useRef();

const handleDownload = async () => {
  if (!carnetRef.current) return;
  const canvas = await html2canvas(carnetRef.current, {
    backgroundColor: null,
    useCORS: true // <-- importante para imágenes externas
  });
  const link = document.createElement('a');
  link.download = `carnet_${member.name || member.id}.png`;
  link.href = canvas.toDataURL();
  link.click();
};

  if (!isOpen || !member) return null;

  const avatar = member.photoUrl
    ? <img src={member.photoUrl} alt="Foto" className="w-16 h-16 rounded-full object-cover border-4 border-blue-200 shadow-lg" />
    : (
      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-blue-200">
        <span className="text-3xl font-bold text-blue-600">{member.name ? member.name.charAt(0).toUpperCase() : '?'}</span>
      </div>
    );

  const qrValue = member.id ? `SOCIO-${member.id}` : member.email || '';

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl p-0 shadow-2xl w-full max-w-sm relative overflow-hidden"
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors duration-200 z-10"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
          {/* Botón para descargar carnet */}
          <button
            onClick={handleDownload}
            className="absolute top-4 left-4 p-2 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors duration-200 z-10 flex items-center gap-1"
            title="Descargar carnet"
          >
            <Download className="w-5 h-5 text-blue-600" />
          </button>
          {/* Carnet visual */}
          <div ref={carnetRef} className="flex flex-col items-center justify-center py-8 px-6 bg-white/90 rounded-3xl shadow-lg relative">
            <div className="w-full h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-3xl flex items-center justify-center mb-6 relative">
              <div className="absolute left-6 top-1/2 -translate-y-1/2">
                {avatar}
              </div>
              <h2 className="ml-24 text-xl font-bold text-white tracking-wide">Carnet de Socio</h2>
            </div>
            <div className="w-full mt-8 space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Nombre:</span>
                <span className="text-gray-900">{member.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">RUT:</span>
                <span className="text-gray-900">{member.rut || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Email:</span>
                <span className="flex items-center gap-1 text-gray-900">
                  <Mail className="w-4 h-4" /> {member.email || '-'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Dirección:</span>
                <span className="text-gray-900">{member.address || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Teléfono:</span>
                <span className="flex items-center gap-1 text-gray-900">
                  <Phone className="w-4 h-4" /> {member.phone || '-'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Membresía:</span>
                <span className="text-gray-900 capitalize">{member.membershipType || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Estado:</span>
                <span className="text-gray-900 capitalize">{member.status || '-'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Fecha de nacimiento:</span>
                <span className="text-gray-900">
                  {member.birthDate ? new Date(member.birthDate).toLocaleDateString('es-CL') : '-'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Fecha de ingreso:</span>
                <span className="text-gray-900">
                  {member.joinDate ? new Date(member.joinDate).toLocaleDateString('es-CL') : '-'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700">Familia:</span>
                <span className="text-gray-900">
                  {`${member.familia_adultos || 0} Adultos${(member.familia_ninos || 0) > 0 ? ` y ${member.familia_ninos} ${member.familia_ninos === 1 ? 'Niño' : 'Niños'}` : ''}`}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center w-full mt-8">
              <QRCodeSVG value={qrValue} size={80} className="mb-2 bg-white p-2 rounded-lg shadow" />
              <div className="w-full h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-b-3xl flex items-center justify-center">
                <span className="text-white text-xs tracking-widest">Socio Chungwha Club</span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MemberProfileModal;